import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  Edit3,
  MousePointer,
  Move,
  MessageCircle,
  PenTool,
  Type,
  Palette,
  Ruler,
  Monitor,
  Clock,
  Users,
  Activity,
  MapPin,
  Settings,
  WifiOff,
  Smartphone,
  Tablet,
  Tv,
} from "lucide-react";

interface UserPresenceData {
  userId: string;
  username: string;
  avatar?: string;
  status: PresenceStatus;
  activity: UserActivity;
  location: UserLocation;
  devices: UserDevice[];
  joinedAt: Date;
  lastActiveAt: Date;
  totalSessionTime: number;
  isIdle: boolean;
  permissions: UserPermissions;
  preferences: PresencePreferences;
}

enum PresenceStatus {
  ONLINE = "online",
  AWAY = "away",
  BUSY = "busy",
  IDLE = "idle",
  OFFLINE = "offline",
}

enum ActivityType {
  VIEWING = "viewing",
  EDITING = "editing",
  SELECTING = "selecting",
  DRAGGING = "dragging",
  COMMENTING = "commenting",
  ANNOTATING = "annotating",
  TYPING = "typing",
  PAINTING = "painting",
  DRAWING = "drawing",
  MEASURING = "measuring",
  PRESENTING = "presenting",
  REVIEWING = "reviewing",
  DEBUGGING = "debugging",
  IDLE = "idle",
}

interface UserActivity {
  currentAction: ActivityType;
  targetComponentId?: string;
  isCollaborative: boolean;
  collaborators?: string[];
  startedAt: Date;
}

interface UserLocation {
  viewport: {
    x: number;
    y: number;
    zoom: number;
    width: number;
    height: number;
  };
  cursor: {
    x: number;
    y: number;
    componentId?: string;
  };
  focusedComponent?: string;
  selectedComponents: string[];
  currentLayer: number;
}

interface UserDevice {
  type: "desktop" | "tablet" | "mobile" | "tv";
  browser: string;
  os: string;
  touchEnabled: boolean;
}

interface UserPermissions {
  canEdit: boolean;
  canComment: boolean;
  canDelete: boolean;
  canShare: boolean;
  canAdmin: boolean;
}

interface PresencePreferences {
  showActivity: boolean;
  showLocation: boolean;
  privacyMode: "full" | "limited" | "private";
}

interface EnhancedPresenceProps {
  users: UserPresenceData[];
  currentUserId: string;
  onUserClick?: (userId: string) => void;
  showDetailedInfo?: boolean;
  compactMode?: boolean;
}

export const EnhancedPresence: React.FC<EnhancedPresenceProps> = ({
  users,
  currentUserId,
  onUserClick,
  showDetailedInfo = true,
  compactMode = false,
}) => {
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"activity" | "joinTime" | "name">(
    "activity",
  );

  const getActivityIcon = (activity: ActivityType, size: number = 16) => {
    const className = `w-${size / 4} h-${size / 4}`;

    switch (activity) {
      case ActivityType.VIEWING:
        return <Eye className={className} />;
      case ActivityType.EDITING:
        return <Edit3 className={className} />;
      case ActivityType.SELECTING:
        return <MousePointer className={className} />;
      case ActivityType.DRAGGING:
        return <Move className={className} />;
      case ActivityType.COMMENTING:
        return <MessageCircle className={className} />;
      case ActivityType.ANNOTATING:
        return <PenTool className={className} />;
      case ActivityType.TYPING:
        return <Type className={className} />;
      case ActivityType.PAINTING:
        return <Palette className={className} />;
      case ActivityType.MEASURING:
        return <Ruler className={className} />;
      case ActivityType.PRESENTING:
        return <Monitor className={className} />;
      case ActivityType.IDLE:
        return <Clock className={className} />;
      default:
        return <Activity className={className} />;
    }
  };

  const getDeviceIcon = (deviceType: string, size: number = 16) => {
    const className = `w-${size / 4} h-${size / 4}`;

    switch (deviceType) {
      case "mobile":
        return <Smartphone className={className} />;
      case "tablet":
        return <Tablet className={className} />;
      case "tv":
        return <Tv className={className} />;
      default:
        return <Monitor className={className} />;
    }
  };

  const getStatusColor = (status: PresenceStatus): string => {
    switch (status) {
      case PresenceStatus.ONLINE:
        return "bg-green-400";
      case PresenceStatus.BUSY:
        return "bg-red-400";
      case PresenceStatus.AWAY:
        return "bg-yellow-400";
      case PresenceStatus.IDLE:
        return "bg-gray-400";
      case PresenceStatus.OFFLINE:
        return "bg-gray-300";
      default:
        return "bg-gray-300";
    }
  };

  const getActivityColor = (activity: ActivityType): string => {
    switch (activity) {
      case ActivityType.EDITING:
        return "text-blue-600 bg-blue-100";
      case ActivityType.COMMENTING:
        return "text-green-600 bg-green-100";
      case ActivityType.ANNOTATING:
        return "text-purple-600 bg-purple-100";
      case ActivityType.DRAGGING:
        return "text-orange-600 bg-orange-100";
      case ActivityType.SELECTING:
        return "text-indigo-600 bg-indigo-100";
      case ActivityType.PRESENTING:
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const formatDuration = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    switch (sortBy) {
      case "activity":
        if (a.isIdle !== b.isIdle) return a.isIdle ? 1 : -1;
        return b.lastActiveAt.getTime() - a.lastActiveAt.getTime();
      case "joinTime":
        return a.joinedAt.getTime() - b.joinedAt.getTime();
      case "name":
        return a.username.localeCompare(b.username);
      default:
        return 0;
    }
  });

  if (compactMode) {
    return (
      <div className="flex flex-wrap gap-2">
        {sortedUsers.map((user) => (
          <motion.div
            key={user.userId}
            whileHover={{ scale: 1.05 }}
            className="relative"
          >
            <div
              className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden cursor-pointer relative"
              onClick={() => onUserClick?.(user.userId)}
            >
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-600 text-sm font-medium">
                  {user.username[0].toUpperCase()}
                </div>
              )}

              {/* Status indicator */}
              <div
                className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${getStatusColor(user.status)}`}
              />

              {/* Activity indicator */}
              {user.activity.isCollaborative && (
                <div className="absolute -top-1 -left-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <Users className="w-2 h-2 text-white" />
                </div>
              )}
            </div>

            {/* Tooltip */}
            <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
              {user.username} - {user.activity.currentAction}
            </div>
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-900">
              Collaboration ({users.length})
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as "activity" | "joinTime" | "name")}
              className="text-sm border border-gray-200 rounded px-2 py-1"
            >
              <option value="activity">By Activity</option>
              <option value="joinTime">By Join Time</option>
              <option value="name">By Name</option>
            </select>

            <Settings className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" />
          </div>
        </div>
      </div>

      {/* Users list */}
      <div className="max-h-96 overflow-y-auto">
        <AnimatePresence>
          {sortedUsers.map((user) => (
            <motion.div
              key={user.userId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                user.userId === currentUserId ? "bg-blue-50" : ""
              }`}
              onClick={() => {
                setSelectedUser(
                  selectedUser === user.userId ? null : user.userId,
                );
                onUserClick?.(user.userId);
              }}
            >
              <div className="flex items-start space-x-3">
                {/* Avatar and status */}
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600 font-medium">
                        {user.username[0].toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Status indicator */}
                  <div
                    className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white ${getStatusColor(user.status)}`}
                  >
                    {user.status === PresenceStatus.OFFLINE && (
                      <WifiOff className="w-2 h-2 text-white m-0.5" />
                    )}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  {/* User info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span
                        className={`font-medium ${user.userId === currentUserId ? "text-blue-600" : "text-gray-900"}`}
                      >
                        {user.username}
                        {user.userId === currentUserId && " (You)"}
                      </span>

                      {user.permissions.canAdmin && (
                        <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                          Admin
                        </span>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Device indicator */}
                      {getDeviceIcon(user.devices[0]?.type || "desktop")}

                      {/* Collaborative indicator */}
                      {user.activity.isCollaborative && (
                        <div className="flex items-center space-x-1 text-blue-600">
                          <Users className="w-3 h-3" />
                          <span className="text-xs">
                            +{user.activity.collaborators?.length || 0}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Activity info */}
                  <div className="flex items-center space-x-2 mt-1">
                    <div
                      className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${getActivityColor(user.activity.currentAction)}`}
                    >
                      {getActivityIcon(user.activity.currentAction, 12)}
                      <span className="capitalize">
                        {user.activity.currentAction.replace("_", " ")}
                      </span>
                    </div>

                    {user.activity.targetComponentId && (
                      <div className="flex items-center space-x-1 text-gray-500 text-xs">
                        <MapPin className="w-3 h-3" />
                        <span>
                          Component #{user.activity.targetComponentId.slice(-6)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Session info */}
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                    <span>
                      Joined{" "}
                      {formatDuration(Date.now() - user.joinedAt.getTime())} ago
                    </span>

                    {user.isIdle ? (
                      <span className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>Idle</span>
                      </span>
                    ) : (
                      <span className="flex items-center space-x-1">
                        <Activity className="w-3 h-3" />
                        <span>Active</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Detailed info (expandable) */}
              <AnimatePresence>
                {selectedUser === user.userId && showDetailedInfo && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mt-4 pt-4 border-t border-gray-200"
                  >
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      {/* Location info */}
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">
                          Location
                        </h4>
                        <div className="space-y-1 text-gray-600">
                          <div>
                            Zoom:{" "}
                            {Math.round(user.location.viewport.zoom * 100)}%
                          </div>
                          <div>Layer: {user.location.currentLayer}</div>
                          <div>
                            Selected: {user.location.selectedComponents.length}
                          </div>
                        </div>
                      </div>

                      {/* Device info */}
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">
                          Device
                        </h4>
                        <div className="space-y-1 text-gray-600">
                          <div className="flex items-center space-x-2">
                            {getDeviceIcon(
                              user.devices[0]?.type || "desktop",
                              12,
                            )}
                            <span className="capitalize">
                              {user.devices[0]?.type}
                            </span>
                          </div>
                          <div>{user.devices[0]?.browser}</div>
                          <div>{user.devices[0]?.os}</div>
                        </div>
                      </div>

                      {/* Permissions */}
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">
                          Permissions
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {user.permissions.canEdit && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                              Edit
                            </span>
                          )}
                          {user.permissions.canComment && (
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                              Comment
                            </span>
                          )}
                          {user.permissions.canShare && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                              Share
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Activity stats */}
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">
                          Session
                        </h4>
                        <div className="space-y-1 text-gray-600">
                          <div>
                            Duration: {formatDuration(user.totalSessionTime)}
                          </div>
                          <div>
                            Last active:{" "}
                            {formatDuration(
                              Date.now() - user.lastActiveAt.getTime(),
                            )}{" "}
                            ago
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Collaborative activities */}
                    {user.activity.isCollaborative &&
                      user.activity.collaborators && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h4 className="font-medium text-gray-700 mb-2">
                            Collaborating with:
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {user.activity.collaborators.map(
                              (collaboratorId) => {
                                const collaborator = users.find(
                                  (u) => u.userId === collaboratorId,
                                );
                                return collaborator ? (
                                  <div
                                    key={collaboratorId}
                                    className="flex items-center space-x-1 px-2 py-1 bg-blue-50 rounded-full"
                                  >
                                    <div className="w-4 h-4 rounded-full bg-gray-200 overflow-hidden">
                                      {collaborator.avatar ? (
                                        <img
                                          src={collaborator.avatar}
                                          alt={collaborator.username}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center text-xs">
                                          {collaborator.username[0]}
                                        </div>
                                      )}
                                    </div>
                                    <span className="text-xs text-blue-700">
                                      {collaborator.username}
                                    </span>
                                  </div>
                                ) : null;
                              },
                            )}
                          </div>
                        </div>
                      )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default EnhancedPresence;
